import {
	BenefitCalculationEnum,
	InsurancePremiumRecipientDto,
	ProductCommissionTypeEnum,
	ProductOfferBenefitRuleDto,
	ProductPremiumCalculationEnum,
	ProductRelationToPrincipalEnum,
	ProductRuleTypeEnum,
	ProductTypeEnum
} from './../../../../../../service/dto-insurance';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { HttpInsuranceProductService } from '../../../../../../service/http-insurance-product.service';
import { DynamicEntityTypeEnum } from '../../../../../../service/dto';

@Component({
	selector: 'app-product-edit-offer-modal',
	standalone: false,
	templateUrl: './product-edit-offer-modal.component.html',
	styleUrl: './product-edit-offer-modal.component.scss'
})
export class ProductEditOfferModalComponent implements OnInit, OnDestroy {
	@Input() productType!: ProductTypeEnum;
	@Input() formArray!: FormArray;
	@Input() formControl?: FormGroup;
	@Input() allRecipients: InsurancePremiumRecipientDto[] = [];

	form!: FormGroup;

	/** Expand state: lineExpanded[lineIndex] */
	lineExpanded: boolean[] = [];
	/** Expand state: lineDetailExpanded[lineIndex][detailIndex] */
	lineDetailExpanded: boolean[][] = [];

	commissionRecipients: InsurancePremiumRecipientDto[] = [];
	recipientsLoading = true;

	availableRuleTypes: ProductRuleTypeEnum[] = [
		'EntryAgeFrom', 'EntryAgeTo', 'MaxAge', 'InstanceCount',
		'WaitingPeriodSuicide', 'WaitingPeriodAccidental',
		'WaitingPeriodNatural', 'WaitingPeriodPreExistingCondition'
	];
	availableAppliedToFoRules: ProductRelationToPrincipalEnum[] = [
		'MainMember', 'Spouse', 'Child', 'ChildStillBorn', 'ChildStudent', 'ChildDisabled', 'All'
	];
	availableAppliedToForBenefitSplits: ProductRelationToPrincipalEnum[] = [
		'Spouse', 'Child', 'ChildStillBorn', 'ChildDisabled', 'ChildStudent'
	];
	availablePremiumTypes: ProductPremiumCalculationEnum[] = ['Fixed', 'Percentage'];
	availableBenefitCalcTypes: BenefitCalculationEnum[] = ['Fixed', 'Percentage', 'Dynamic'];
	availableCommissionTypes: ProductCommissionTypeEnum[] = ['Fixed', 'Percentage'];
	availableOfferLineNames: string[] = [];
	recipients: InsurancePremiumRecipientDto[] = [];

	compareById = (o1: any, o2: any): boolean => {
		if (o1 == undefined && o2 == undefined) {
			return true;
		}
		return o1?.id === o2?.id;
	};
	compareByType = (o1: any, o2: any): boolean => {
		if (o1 == undefined) {
			o1 = 'MainMember';
		}
		if (o2 == undefined) {
			o2 = 'MainMember';
		}
		return o1 == o2;
	};

	constructor(
		private fb: FormBuilder,
		private modalRef: NzModalRef,
		private httpService: HttpInsuranceProductService
	) { }

	ngOnInit(): void {
		this.httpService.getOfferLineNames().subscribe(result => this.availableOfferLineNames = result);
		const existing = this.formControl?.getRawValue();

		this.form = this.fb.group({
			id: [existing?.id ?? ''],
			name: [existing?.name ?? '', Validators.required],
			description: [existing?.description ?? ''],
			rules: this.fb.array((existing?.rules ?? []).map((r: any) => this.buildRuleGroup(r))),
			lines: this.fb.array((existing?.lines ?? []).map((l: any) => this.buildLineGroup(l))),
			benefitSplits: this.fb.array((existing?.benefitSplits ?? []).map((b: any) => this.buildBenefitSplitGroup(b))),
			benefitRules: this.fb.array((existing?.benefitRules ?? []).map((b: any) => this.buildOfferBenefitRuleGroup(b))),
		});

		// Initialise expand state tracking
		const lines = existing?.lines ?? [];
		this.lineExpanded = lines.map(() => false);
		this.lineDetailExpanded = lines.map((l: any) => (l.details ?? []).map(() => false));

		if (this.allRecipients.length == 0) {
			this.httpService.getPremiumRecipients().subscribe(recipients => {
				this.allRecipients = recipients;
			});
		}
	}

	ngOnDestroy(): void { }

	// ─── Cast helper ─────────────────────────────────────────────────────────

	asGroup(ctrl: AbstractControl): FormGroup {
		return ctrl as FormGroup;
	}

	// ─── FormArray accessors ──────────────────────────────────────────────────

	get rulesArray(): FormArray {
		return this.form.get('rules') as FormArray;
	}

	get linesArray(): FormArray {
		return this.form.get('lines') as FormArray;
	}

	get benefitSplitsArray(): FormArray {
		return this.form.get('benefitSplits') as FormArray;
	}
	get benefitRuleArray(): FormArray {
		return this.form.get('benefitRules') as FormArray;
	}

	getLineDetails(lineIndex: number): FormArray {
		return this.linesArray.at(lineIndex).get('details') as FormArray;
	}

	getDetailCommissions(lineIndex: number, detailIndex: number): FormArray {
		return this.getLineDetails(lineIndex).at(detailIndex).get('commissions') as FormArray;
	}

	// ─── Group builders ───────────────────────────────────────────────────────

	private buildRuleGroup(item?: any): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			ruleType: [item?.ruleType ?? '', Validators.required],
			appliedTo: [item?.appliedTo ?? '', Validators.required],
			threshold: [item?.threshold ?? 0, Validators.required],
		});
	}

	private buildLineGroup(item?: any): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			name: [item?.name ?? '', Validators.required],
			description: [item?.description ?? ''],
			compulsory: [item?.compulsory ?? false],
			details: this.fb.array((item?.details ?? []).map((d: any) => this.buildLineDetailGroup(d))),
		});
	}

	private buildLineDetailGroup(item?: any): FormGroup {
		let group = this.fb.group({
			id: [item?.id ?? ''],
			fromDate: [item?.fromDate ? new Date(item.fromDate) : null, Validators.required],
			toDate: [item?.toDate ? new Date(item.toDate) : null],
			premiumType: [item?.premiumType ?? 'Fixed', Validators.required],
			premiumAmount: [item?.premiumAmount ?? 0, Validators.required],
			benefitType: [item?.benefitType ?? 'Fixed', Validators.required],
			appliesOnlyToNewPolicies: [item?.appliesOnlyToNewPolicies ?? false],
			commissions: this.fb.array((item?.commissions ?? []).map((c: any) => this.buildCommissionGroup(c))),
		}) as FormGroup;


		if (this.productType == 'Funeral') {
			group.addControl('benefitAmountNatural', this.fb.control(item?.benefitAmountNatural ?? item?.benefitAmount, Validators.required));
			group.addControl('benefitAmountAccidental', this.fb.control(item?.benefitAmountAccidental ?? item?.benefitAmount, Validators.required));
			group.addControl('benefitAmountSuicide', this.fb.control(item?.benefitAmountSuicide ?? item?.benefitAmount, Validators.required));
		} else {
			group.addControl('benefitAmount', this.fb.control(item?.benefitAmount ?? 0, Validators.required));
		}

		// Add conditional controls if values already exist
		if (item?.premiumType === 'Percentage') {
			group.addControl('premiumPercentageProperty', this.fb.control(item?.premiumPercentageProperty ?? '', Validators.required));
		}
		if (item?.benefitType === 'Percentage') {
			group.addControl('benefitPercentageProperty', this.fb.control(item?.benefitPercentageProperty ?? '', Validators.required));
		}

		// Listen for premiumType changes
		group.get('premiumType')?.valueChanges.subscribe(value => {
			if (value === 'Percentage') {
				group.addControl('premiumPercentageProperty', this.fb.control(item?.premiumPercentageProperty ?? '', Validators.required));
			} else {
				group.removeControl('premiumPercentageProperty');
			}
		});

		// Listen for benefitType changes
		group.get('benefitType')?.valueChanges.subscribe(value => {
			if (value === 'Percentage') {
				group.addControl('benefitPercentageProperty', this.fb.control(item?.benefitPercentageProperty ?? '', Validators.required));
			} else {
				group.removeControl('benefitPercentageProperty');
			}
		});
		return group;
	}

	private buildCommissionGroup(item?: any): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			recipient: [item?.recipient ?? null, Validators.required],
			commissionType: [item?.commissionType ?? 'Fixed', Validators.required],
			commissionAmount: [item?.commissionAmount ?? 0, Validators.required],
		});
	}

	private buildBenefitSplitGroup(item?: any): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			appliedTo: [item?.appliedTo ?? '', Validators.required],
			fromAge: [item?.fromAge ?? 0, Validators.required],
			toAge: [item?.toAge ?? 0, Validators.required],
			benefitAmount: [item?.benefitAmount ?? 0, Validators.required],
		});
	}
	private buildOfferBenefitRuleGroup(item?: ProductOfferBenefitRuleDto): FormGroup {
		console.log('buildOfferBenefitRuleGroup', item);
		if (item?.recipientType == 'Member') {
			return this.fb.group({
				id: [item?.id ?? ''],
				from: [item?.from ?? 0, Validators.required],
				to: [item?.to ?? 0, Validators.required],
				amount: [item?.amount ?? 0, Validators.required],
				recipientType: [item?.recipientType ?? 'Member'],
				calculationType: ['Percentage'],
			});
		}
		return this.fb.group({
			id: [item?.id ?? ''],
			from: [item?.from ?? 0, Validators.required],
			to: [item?.to ?? 0, Validators.required],
			amount: [item?.amount ?? 0, Validators.required],
			recipientType: [item?.recipientType ?? 'Member'],
			recipient: [item?.recipient],
			calculationType: ['Percentage'],
		});
	}

	// ─── Rules CRUD ───────────────────────────────────────────────────────────

	addRule(): void {
		this.rulesArray.push(this.buildRuleGroup());
	}

	removeRule(index: number): void {
		this.rulesArray.removeAt(index);
	}

	// ─── Lines CRUD ───────────────────────────────────────────────────────────

	addLine(): void {
		this.linesArray.push(this.buildLineGroup());
		this.lineExpanded.push(false);
		this.lineDetailExpanded.push([]);
	}

	removeLine(index: number): void {
		this.linesArray.removeAt(index);
		this.lineExpanded.splice(index, 1);
		this.lineDetailExpanded.splice(index, 1);
	}

	// ─── Line Details CRUD ────────────────────────────────────────────────────

	addLineDetail(lineIndex: number): void {
		this.getLineDetails(lineIndex).push(this.buildLineDetailGroup());
		this.lineExpanded[lineIndex] = true;
		this.lineDetailExpanded[lineIndex].push(false);
	}

	removeLineDetail(lineIndex: number, detailIndex: number): void {
		this.getLineDetails(lineIndex).removeAt(detailIndex);
		this.lineDetailExpanded[lineIndex].splice(detailIndex, 1);
	}

	// ─── Commissions CRUD ─────────────────────────────────────────────────────

	addCommission(lineIndex: number, detailIndex: number): void {
		this.getDetailCommissions(lineIndex, detailIndex).push(this.buildCommissionGroup());
		this.lineDetailExpanded[lineIndex][detailIndex] = true;
	}

	removeCommission(lineIndex: number, detailIndex: number, commissionIndex: number): void {
		this.getDetailCommissions(lineIndex, detailIndex).removeAt(commissionIndex);
	}

	// ─── Benefit Splits CRUD ──────────────────────────────────────────────────

	addBenefitSplit(): void {
		this.benefitSplitsArray.push(this.buildBenefitSplitGroup());
	}

	removeBenefitSplit(index: number): void {
		this.benefitSplitsArray.removeAt(index);
	}

	// ─── Benefit Rule CRUD ──────────────────────────────────────────────────

	addBenefitRule(): void {
		this.benefitRuleArray.push(this.buildOfferBenefitRuleGroup());
	}

	removeBenefitRule(index: number): void {
		this.benefitRuleArray.removeAt(index);
	}


	// ─── Modal actions ────────────────────────────────────────────────────────

	onCancel(): void {
		this.modalRef.close();
	}

	onSave(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const offer = this.form.getRawValue();

		if (this.formControl) {
			this.formControl.patchValue({ id: offer.id, name: offer.name, description: offer.description });
			this.rebuildFormArray(this.formControl.get('rules') as FormArray, offer.rules, (r) => this.buildRuleGroup(r));
			this.rebuildFormArray(this.formControl.get('lines') as FormArray, offer.lines, (l) => this.buildLineGroup(l));
			this.rebuildFormArray(this.formControl.get('benefitSplits') as FormArray, offer.benefitSplits, (b) => this.buildBenefitSplitGroup(b));
			this.rebuildFormArray(this.formControl.get('benefitRules') as FormArray, offer.benefitRules, (b: any) => this.buildOfferBenefitRuleGroup(b));
		} else {
			this.formArray.push(this.fb.group({
				id: [offer.id],
				name: [offer.name, Validators.required],
				description: [offer.description],
				rules: this.fb.array(offer.rules.map((r: any) => this.buildRuleGroup(r))),
				lines: this.fb.array(offer.lines.map((l: any) => this.buildLineGroup(l))),
				benefitSplits: this.fb.array(offer.benefitSplits.map((b: any) => this.buildBenefitSplitGroup(b))),
				benefitRules: this.fb.array(offer.benefitRules.map((b: any) => this.buildOfferBenefitRuleGroup(b))),
			}));
		}

		this.modalRef.close(offer);
	}

	private rebuildFormArray<T>(target: FormArray, items: T[], builder: (item: T) => FormGroup): void {
		target.clear();
		items.forEach(item => target.push(builder(item)));
	}
	getDetailGroup(lineIndex: number, detailIndex: number): FormGroup {
		return (
			(this.form.get('lines') as FormArray)
				.at(lineIndex)
				.get('details') as FormArray
		).at(detailIndex) as FormGroup;
	}

	getPremiumType(lineIndex: number, detailIndex: number): string {
		return this.getDetailGroup(lineIndex, detailIndex).get('premiumType')?.value;
	}

	getBenefitType(lineIndex: number, detailIndex: number): string {
		return this.getDetailGroup(lineIndex, detailIndex).get('benefitType')?.value;
	}

	getRulePeriodLabelSuffix(): string {
		if (this.productType == 'TemporaryDisability') {
			return 'Month';
		}
		if (this.productType == 'PermanentHealthInsurance') {
			return 'Age';
		}
		return '';
	}
	getRecipientType(ctrl: AbstractControl): string | undefined {
		return this.asGroup(ctrl).get('recipientType')?.value;
	}
	// onRecipientTypeChange(ctrl: AbstractControl): void {
	// 	const type = this.getRecipientType(ctrl);
	// 	if (!type) {
	// 		this.recipients = [];
	// 	} else {
	// 		this.recipients = this.allRecipients.filter(item => item.type == type);
	// 	}
	// }
	getAllErrors(form: AbstractControl): Record<string, any> | null {
		if (form instanceof FormControl) {
			return form.errors;
		}

		const errors: Record<string, any> = {};

		if (form instanceof FormGroup) {
			Object.keys(form.controls).forEach(key => {
				const childErrors = this.getAllErrors(form.controls[key]);
				if (childErrors) errors[key] = childErrors;
			});
		}

		if (form instanceof FormArray) {
			form.controls.forEach((control, i) => {
				const childErrors = this.getAllErrors(control);
				if (childErrors) errors[i] = childErrors;
			});
		}

		return Object.keys(errors).length ? errors : null;
	}

}
